import { useState, useContext } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import ReferredByDrawer from '../drawers/ReferredByDrawer';
import { referringProfessionalName } from '~/components/CampaignRequestDetails/ReferringProfessionalField';
import AgreementsDrawer from '../drawers/AgreementsDrawer';
import { CampaignContext } from '~/context/CampaignProvider';

interface Props {
  campaignRequest: any;
  patient: any;
  metadata: any;
  application: any;
  approval?: any;
  onCampaignRequestUpdate?: (updatedCampaignRequest: any) => void;
}

const CampaignRequestTab: React.FC<Props> = ({
  campaignRequest,
  patient,
  metadata,
  application,
  approval,
  onCampaignRequestUpdate,
}) => {
  const [isReferredByDrawerOpen, setIsReferredByDrawerOpen] = useState(false);
  const [isAgreementsDrawerOpen, setIsAgreementsDrawerOpen] = useState(false);

  const contextValue = useContext(CampaignContext);
  const [state, campaignDispatch] = (contextValue as unknown as [
    { campaignData: any },
    any
  ]) || [{ campaignData: null }, () => {}];
  const campaignData = state?.campaignData;

  return (
    <>
      <div className='p-[12px] pt-0'>
        <div className='flex justify-between'>
          <h2 className='text-[22px]/[28px] font-bold'>Request</h2>
          {campaignRequest?.id && (
            <Link
              to={`/campaign-requests/${campaignRequest.id}`}
              className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white text-center'
            >
              View Request
            </Link>
          )}
        </div>

        <div className='flex justify-between'>
          <div className='grow'>
            <div className='mb-[16px]'>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Campaign Request
              </label>
              <div>{campaignRequest?.status?.name}</div>
            </div>
            <div className='mb-[16px]'>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Community
              </label>
              <div>
                {campaignRequest?.community_types &&
                  campaignRequest?.community_types.map((item, i) => {
                    return <div key={i}>{item.name}</div>;
                  })}
              </div>
            </div>
            <div>
              <label className='text-[12px]/[16px] text-[#949599]'>
                CS Approver
              </label>
              <div>
                {approval?.ps_approver_person 
                  ? `${approval.ps_approver_person.first_name} ${approval.ps_approver_person.last_name}`.trim()
                  : 'Not assigned'}
              </div>
              <div className='text-sm'>
                {approval?.ps_approved_date
                  ? format(parseISO(approval.ps_approved_date), 'PPP')
                  : 'Not approved'}
              </div>
            </div>
          </div>
          <div className='grow'>
            <div className='mb-[16px]'>
              <div className='flex items-center gap-[8px]'>
                <label className='text-[12px]/[16px] text-[#949599]'>
                  Referred by
                </label>
                {campaignRequest?.id && (
                  <button
                    type='button'
                    onClick={() => setIsReferredByDrawerOpen(true)}
                    className='text-[12px]/[16px] font-bold text-primary hover:underline'
                  >
                    Edit
                  </button>
                )}
              </div>
              <div>
                {campaignRequest?.heard_of_source?.name || ''}
                {campaignRequest?.heard_of_source?.code === 'other_prof' &&
                  campaignRequest?.heard_of_source_other &&
                  ` - ${campaignRequest.heard_of_source_other}`}
              </div>
            </div>
            {referringProfessionalName(campaignRequest) && (
              <div className='mb-[16px]'>
                <label className='text-[12px]/[16px] text-[#949599]'>
                  Referring Medical Professional
                </label>
                <div>{referringProfessionalName(campaignRequest)}</div>
              </div>
            )}
            <div className='mb-[16px]'>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Terms of Service Accepted
              </label>
              <div>{application?.ag_terms_of_service ? 'Yes' : 'No'}</div>
            </div>
            <div>
              <label className='text-[12px]/[16px] text-[#949599]'>
                Aggreements Signed
              </label>
              <div>{application?.ag_signature_date ? 'Yes' : 'No'}</div>
              <div>
                <a
                  href='#'
                  className='text-primary'
                  onClick={(e) => {
                    e.preventDefault();
                    setIsAgreementsDrawerOpen(true);
                  }}
                >
                  View{' '}
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='18'
                    height='18'
                    viewBox='0 0 18 18'
                    fill='none'
                  >
                    <path
                      d='M13.5 15.75L16.5 12.75L15.45 11.7L14.25 12.9V9.76875H12.75V12.9L11.55 11.7L10.5 12.75L13.5 15.75ZM10.5 18V16.5H16.5V18H10.5ZM4.5 15C4.0875 15 3.73438 14.8531 3.44063 14.5594C3.14688 14.2656 3 13.9125 3 13.5V3C3 2.5875 3.14688 2.23438 3.44063 1.94063C3.73438 1.64688 4.0875 1.5 4.5 1.5H9.75L14.25 6V8.26875H12.75V6.75H9V3H4.5V13.5H9V15H4.5Z'
                      fill='#00AEB3'
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReferredByDrawer
        isOpen={isReferredByDrawerOpen}
        setIsOpen={setIsReferredByDrawerOpen}
        campaignRequest={campaignRequest}
        metadata={metadata}
        onCampaignRequestUpdate={onCampaignRequestUpdate}
      />

      <AgreementsDrawer
        isOpen={isAgreementsDrawerOpen}
        setIsOpen={setIsAgreementsDrawerOpen}
        application={application}
        applicationId={application?.id}
        onUpdate={(response) => {
          // The response includes complete campaign data; update context directly
          const {
            campaign: updatedCampaign,
            campaign_id,
            patient: updatedPatient,
            csc: updatedCsc,
            campaign_request: updatedCampaignRequest,
            notes: updatedNotes,
            coordinator: updatedCoordinator,
            campaign_approval: updatedCampaignApproval,
            financial_coordinator: updatedFinancialCoordinator,
            ...applicationFields
          } = response;

          const updatedApplication = {
            ...applicationFields,
            campaign: campaign_id,
          };

          campaignDispatch({
            type: 'set_campaign',
            payload: {
              campaign: updatedCampaign,
              patient: updatedPatient,
              csc: updatedCsc,
              campaign_request: updatedCampaignRequest,
              notes: updatedNotes,
              campaignPhotoInfo: campaignData?.campaignPhotoInfo || null,
              coordinator: updatedCoordinator,
              financial_coordinator:
                updatedFinancialCoordinator ||
                campaignData?.financial_coordinator ||
                null,
              count: campaignData?.count || 0,
              application: updatedApplication,
              campaign_approval: updatedCampaignApproval,
            },
          });
        }}
      />
    </>
  );
};

export default CampaignRequestTab;
